import * as React from "react";
import { Shield, Mail, FileText, ChevronRight, House } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
const policySections = [{
  id: "data-collection",
  title: "1. Hvilke personopplysninger vi samler inn",
  content: "Når du bruker Xora sine AI-tjenester for eiendomsmeglere, samler vi inn følgende opplysninger for å kunne levere og forbedre våre tjenester:",
  list: ["Identifikasjons- og kontaktinformasjon (navn, e-postadresse, telefonnummer, tilknyttet meglerkontor).", "Tekniske data og bruksmønster (IP-adresse, innloggingsdata, nettlesertype, enhetsinformasjon).", "Innhold og eiendomsdata du laster opp for prosessering av våre AI-modeller (vi iverksetter tiltak for å anonymisere dette der det er hensiktsmessig).", "Fakturerings- og betalingsinformasjon ved kjøp av abonnement."]
}, {
  id: "legal-basis",
  title: "2. Rettslig grunnlag og formål (GDPR)",
  content: "Vi behandler dine personopplysninger i strengt samsvar med personvernforordningen (GDPR). Vårt rettslige grunnlag for behandlingen er:",
  list: ["Oppfyllelse av avtale (GDPR art. 6 nr. 1 bokstav b) for å levere våre AI-automatiseringsløsninger til deg og ditt byrå.", "Berettiget interesse (GDPR art. 6 nr. 1 bokstav f) for å forbedre våre algoritmer, sikre systemene våre mot svindel, og analysere bruk for å optimalisere brukeropplevelsen.", "Samtykke (GDPR art. 6 nr. 1 bokstav a) for utsendelse av markedsføring og bruk av ikke-essensielle informasjonskapsler."]
}, {
  id: "data-retention",
  title: "3. Datalagring og sletting",
  content: "Vi lagrer dine personopplysninger kun så lenge det er nødvendig for formålet de ble samlet inn for. Kundedata knyttet til aktive abonnementer beholdes så lenge avtalen løper. Data som brukes til å trene våre AI-modeller anonymiseres fortløpende. Du kan når som helst be om at din konto og tilhørende identifiserbare data slettes fra våre systemer."
}, {
  id: "third-parties",
  title: "4. Deling med tredjeparter",
  content: "Xora selger aldri dine personopplysninger. Vi deler kun data med betrodde underleverandører (databehandlere) som er strengt nødvendige for å drifte tjenesten, for eksempel skytjenesteleverandører og leverandører av AI-infrastruktur. Alle våre databehandlere er underlagt strenge databehandleravtaler som sikrer full GDPR-etterlevelse og at data ikke brukes til egne formål."
}, {
  id: "cookies",
  title: "5. Informasjonskapsler (Cookies)",
  content: "Vi bruker informasjonskapsler for å sikre at plattformen fungerer optimalt, huske dine innstillinger og analysere trafikk. Du kan administrere dine cookie-preferanser i nettleseren din. Nødvendige informasjonskapsler kan ikke velges bort, da disse kreves for kjernefunksjonaliteten og sikkerheten i vår programvare."
}, {
  id: "user-rights",
  title: "6. Dine rettigheter som registrert",
  content: "Som registrert bruker har du en rekke rettigheter etter personvernlovgivningen som vi tar på største alvor:",
  list: ["Rett til innsyn i hvilke opplysninger vi har lagret om deg.", "Rett til retting av feilaktige eller ufullstendige opplysninger.", "Rett til sletting ('retten til å bli glemt') under visse forutsetninger.", "Rett til å begrense eller protestere mot spesifikk behandling av dine data.", "Rett til dataportabilitet (få utlevert dine data i et strukturert, maskinlesbart format)."]
}];
export default function PrivacyPolicyContent() {
  return <section id="privacy-policy-content" className="bg-background text-foreground min-h-screen py-12 md:py-20">
      <div className="container mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
        {}
        <div className="mb-8 md:mb-12">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/" className="flex items-center gap-1.5 hover:text-primary transition-colors">
                    <House className="size-4" />
                    Hjem
                  </Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator>
                <ChevronRight className="size-4" />
              </BreadcrumbSeparator>
              <BreadcrumbItem>
                <BreadcrumbPage className="font-medium">
                  Personvernerklæring
                </BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>

        {}
        <div className="mb-12">
          <div className="inline-flex items-center justify-center p-3 bg-primary/10 rounded-2xl mb-6">
            <Shield className="size-8 text-primary" />
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold font-default tracking-tight mb-4">
            Personvernerklæring
          </h1>
          <p className="text-muted-foreground text-sm md:text-base mb-6">
            Sist oppdatert: 15. oktober 2023
          </p>
          <p className="text-lg text-foreground/80 leading-relaxed">
            Xora ("vi", "oss", "vår") tar ditt personvern på største alvor.
            Denne erklæringen forklarer i detalj hvordan vi samler inn, bruker,
            deler og beskytter dine personopplysninger når du benytter deg av
            våre AI-automatiseringsløsninger skreddersydd for eiendomsmeglere.
          </p>
        </div>

        <Separator className="mb-12" />

        {}
        <div className="space-y-12 mb-16">
          {policySections.map((section, i) => <div data-index={i} key={section.id} className="scroll-mt-24">
              <h2 className="text-2xl font-semibold font-default mb-4 text-foreground">
                {section.title}
              </h2>
              <p className="text-foreground/80 leading-relaxed mb-4">
                {section.content}
              </p>
              {section.list && <ul className="list-disc pl-6 space-y-2 text-foreground/80 marker:text-primary/60">
                  {section.list.map((item, j) => <li data-index={j} key={j} className="leading-relaxed">
                      {item}
                    </li>)}
                </ul>}
            </div>)}
        </div>

        {}
        <Card className="bg-secondary/30 border-border/50 shadow-sm">
          <CardHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-background rounded-lg shadow-xs">
                <Mail className="size-5 text-primary" />
              </div>
              <CardTitle className="text-xl font-default">
                Spørsmål om personvern?
              </CardTitle>
            </div>
            <CardDescription className="text-base">
              Dersom du har spørsmål om hvordan vi behandler dine
              personopplysninger, eller ønsker å benytte deg av dine rettigheter,
              ta gjerne kontakt med vårt personvernombud.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
              <Button asChild size="lg" className="w-full sm:w-auto">
                <Link to="https://xora.no/kontakt/">
                  <Mail className="mr-2 size-4" />
                  Kontakt personvernombud
                </Link>
              </Button>
              <p className="text-sm text-muted-foreground">
                Vi svarer normalt innen 1-2 virkedager.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </section>;
}